// src/pages/Drops.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getDropsNav,
  getDropDetail,
  DropNavDto,
  DropDetailDto,
} from "../api/dropApi";

/* ================= HOOK ================= */
function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

/* ================= TYPES ================= */
type DropPageState = {
  collection: DropDetailDto | null;
  products: any[];
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
};

function safeMsg(e: any) {
  return e?.response?.data?.message || e?.message || "Request failed";
}

/* ================= PAGE ================= */
export default function Drops() {
  const { t } = useTranslation();
  const q = useQuery();
  const navigate = useNavigate();

  // query
  const era = q.get("era") || "new";
  const activeSlug = q.get("drop") || "";

  // nav tabs
  const [tabs, setTabs] = useState<DropNavDto[]>([]);
  const [loadingTabs, setLoadingTabs] = useState(true);

  // detail
  const [data, setData] = useState<DropPageState | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [err, setErr] = useState<string>("");

  // ================= LOAD TABS =================
  useEffect(() => {
    let alive = true;

    setLoadingTabs(true);
    setErr("");

    // ✅ gọi đúng endpoint: GET /api/collections/drops?era=...&isActive=true
    getDropsNav({ era, isActive: true })
      .then((rows) => {
        if (!alive) return;

        const sorted = [...(rows || [])].sort(
          (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
        );
        setTabs(sorted);

        // ✅ Nếu URL không có drop → auto pick drop đầu
        if (!activeSlug && sorted.length > 0) {
          navigate(
            `/drops?era=${encodeURIComponent(era)}&drop=${encodeURIComponent(sorted[0].slug)}`,
            { replace: true }
          );
          return;
        }

        // ✅ Nếu URL có drop nhưng không tồn tại trong list → reset về drop đầu
        if (activeSlug && sorted.length > 0) {
          const ok = sorted.some((x) => x.slug === activeSlug);
          if (!ok) {
            navigate(
              `/drops?era=${encodeURIComponent(era)}&drop=${encodeURIComponent(sorted[0].slug)}`,
              { replace: true }
            );
          }
        }
      })
      .catch((e: any) => {
        if (!alive) return;
        setErr(safeMsg(e) || "Failed to load drops");
      })
      .finally(() => alive && setLoadingTabs(false));

    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [era]);

  // ================= LOAD DETAIL =================
  useEffect(() => {
    let alive = true;
    if (!activeSlug) {
      setData(null);
      setLoadingDetail(false);
      return () => {
        alive = false;
      };
    }

    setLoadingDetail(true);
    setErr("");

    // ✅ gọi đúng endpoint: GET /api/collections/{slug}?page=...&pageSize=...&sort=order
    getDropDetail(activeSlug, { page: 1, pageSize: 9, sort: "order" })
      .then((res) => {
        if (!alive) return;
        setData(res as any);
      })
      .catch((e: any) => {
        if (!alive) return;
        setErr(safeMsg(e) || "Failed to load drop detail");
        setData(null);
      })
      .finally(() => alive && setLoadingDetail(false));

    return () => {
      alive = false;
    };
  }, [activeSlug]);

  // ================= UI HELPERS =================
  const go = (slug: string) =>
    navigate(`/drops?era=${encodeURIComponent(era)}&drop=${encodeURIComponent(slug)}`);

  const meta = data?.collection;

  const hero =
    meta?.heroImageUrl ||
    "https://source.unsplash.com/random/2400x1600?dark,fashion,editorial&sig=901";

  const label = meta?.name || "";
  const season = meta?.season || "";
  const desc = meta?.shortDesc || meta?.description || "";

  const storyTitle = meta?.storyTitle || t("drops.story.title");
  const storyBody = meta?.storyBody || t("drops.story.body");

  const featuredImg =
    meta?.featuredImageUrl ||
    "https://source.unsplash.com/random/1200x1600?streetwear,editorial&sig=999";

  const hasTabs = tabs.length > 0;

  return (
    <div className="bg-brand-ivory text-brand-brown min-h-screen overflow-x-hidden">
      {/* ERROR */}
      {err ? (
        <div className="max-w-7xl mx-auto px-6 pt-10 text-sm text-red-700">
          {err}
        </div>
      ) : null}

      {/* ================= HERO ================= */}
      <section className="relative h-screen">
        <img src={hero} className="absolute inset-0 h-full w-full object-cover" alt="" />
        <div className="absolute inset-0 bg-brand-ivory/85" />

        <div className="relative z-10 h-full flex items-center">
          <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-24">
            {/* TEXT */}
            <div>
              <p className="text-[11px] uppercase tracking-[0.55em] text-brand-brownLight">
                {t("drops.hero.kicker")}
              </p>

              <h1 className="mt-12 text-5xl md:text-6xl font-semibold uppercase tracking-[0.35em] leading-tight">
                {loadingDetail ? "..." : label || (hasTabs ? "" : t("drops.noData.title", "No drop"))}
              </h1>

              <p className="mt-6 text-sm uppercase tracking-[0.45em] text-brand-gold">
                {loadingDetail ? "" : season}
              </p>

              <div className="mt-10 h-px w-24 bg-brand-brown/40" />

              <p className="mt-12 max-w-md text-sm leading-loose text-brand-brownLight">
                {loadingDetail
                  ? ""
                  : desc || (!hasTabs ? t("drops.noData.body", "Hiện chưa có drop nào.") : "")}
              </p>
            </div>

            {/* EMPTY SPACE = LUXURY */}
            <div />
          </div>
        </div>
      </section>

      {/* ================= STICKY DROP NAV ================= */}
      <div className="sticky top-0 z-30 backdrop-blur-md bg-brand-ivory/75 border-b border-brand-brown/10">
        <div className="max-w-7xl mx-auto px-6 py-6 flex justify-center gap-20 flex-wrap">
          {loadingTabs ? (
            <div className="text-[11px] uppercase tracking-[0.45em] text-brand-brownLight">
              Loading...
            </div>
          ) : !hasTabs ? (
            <div className="text-[11px] uppercase tracking-[0.45em] text-brand-brownLight">
              {t("drops.noTabs", "No drop available")}
            </div>
          ) : (
            tabs.map((d) => (
              <button
                key={d.slug}
                onClick={() => go(d.slug)}
                className={`text-[11px] uppercase tracking-[0.45em] pb-2 transition-all
                  ${
                    d.slug === activeSlug
                      ? "text-brand-brown border-b border-brand-gold"
                      : "text-brand-brownLight hover:text-brand-brown"
                  }`}
                aria-label={t("drops.nav.aria", { drop: d.name })}
                title={d.season || d.era || ""}
              >
                {d.name}
              </button>
            ))
          )}
        </div>
      </div>

      {/* ================= STORY + FEATURE ================= */}
      <section className="py-40">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-32 items-center">
          {/* STORY */}
          <div>
            <p className="text-[11px] uppercase tracking-[0.5em] text-brand-brownLight">
              {t("drops.story.kicker")}
            </p>

            <h2 className="mt-12 text-3xl uppercase tracking-[0.35em]">
              {loadingDetail ? "..." : storyTitle}
            </h2>

            <p className="mt-12 text-sm leading-loose text-brand-brownLight max-w-md whitespace-pre-line">
              {loadingDetail ? "" : storyBody}
            </p>
          </div>

          {/* FEATURED PIECE */}
          <div className="relative">
            <div className="aspect-[3/4] rounded-3xl overflow-hidden shadow-[0_50px_120px_rgba(43,31,26,0.18)]">
              <img src={featuredImg} className="h-full w-full object-cover" alt="" />
            </div>

            <p className="absolute -bottom-12 left-0 text-xs uppercase tracking-[0.45em]">
              {t("drops.featured.kicker")}
            </p>
          </div>
        </div>
      </section>

      {/* ================= ASYMMETRIC GRID (products) ================= */}
      <section className="pb-40">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-3 gap-x-14 gap-y-36">
          {(data?.products || []).map((p: any, i: number) => {
            const img =
              p?.imageUrls?.[0] ||
              `https://source.unsplash.com/random/1200x1500?street,fashion&sig=${activeSlug}-${i}`;

            const to = `/product/${p?.productId ?? ""}`;

            return (
              <Link
                to={to}
                key={p?.productId ?? i}
                className={`group ${i % 3 === 1 ? "lg:mt-32" : ""}`}
              >
                <div className="aspect-[4/5] overflow-hidden rounded-2xl">
                  <img
                    src={img}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    alt=""
                  />
                </div>

                <div className="mt-6">
                  <p className="text-[10px] uppercase tracking-[0.45em] text-brand-brownLight">
                    {season}
                  </p>
                  <p className="mt-2 text-sm tracking-wide">
                    {p?.name ||
                      t("drops.grid.piece", { n: String(i + 1).padStart(2, "0") })}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>

        {/* No products state */}
        {!loadingDetail && hasTabs && (data?.products?.length ?? 0) === 0 ? (
          <div className="max-w-7xl mx-auto px-6 pt-14 text-center text-sm text-brand-brownLight">
            {t("drops.noProducts", "Drop này chưa có sản phẩm.")}
          </div>
        ) : null}
      </section>

      {/* ================= EDITORIAL CTA ================= */}
      <section className="py-32 border-t border-brand-brown/10">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-[11px] uppercase tracking-[0.5em] text-brand-brownLight">
            {t("drops.cta.kicker")}
          </p>

          <h3 className="mt-10 text-2xl uppercase tracking-[0.35em]">
            {t("drops.cta.title")}
          </h3>

          {/* <Link
            to="/lookbook"
            className="inline-block mt-14 text-xs uppercase tracking-[0.45em] border-b border-brand-brown pb-2 hover:text-brand-gold hover:border-brand-gold transition"
          >
            {t("drops.cta.link")}
          </Link> */}
        </div>
      </section>
    </div>
  );
}
